'use client';

// NavBadges — the ONE nav-badge source for the ops sidebar.
//
// Every sidebar count pill (Needs you / Monitor / Graph intake) renders the same
// NeedsYouBadge and pulls its number from useOpsBadges(), so the per-item counts
// all originate in one place and SUM to the global "Needs you" total. One source
// = no drift between the sidebar item badges and the header escalation tag.
//
// Visual matches mockups/pages/today.html `.it .cnt` (and `.tag.amber`):
// amber text on an amber tint, bold, pill-rounded, tabular numerals.

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useAuth } from '@/contexts/AuthContext';

/** Tone overrides for the count pill. Defaults to amber (the "needs you" hue). */
export type BadgeTone = 'amber' | 'red' | 'green';

const TONE_CLASS: Record<BadgeTone, string> = {
  amber: 'text-ops-amber bg-[rgba(243,177,78,0.14)]',
  red: 'text-ops-red bg-[rgba(240,109,109,0.14)]',
  green: 'text-ops-green bg-[rgba(16,185,129,0.14)]',
};

interface NeedsYouBadgeProps {
  /** The count to display. Renders null when count <= 0. */
  count: number;
  /** Colour tone; defaults to amber. */
  tone?: BadgeTone;
  className?: string;
}

/**
 * A small count pill. Amber by default; pass tone to override. Renders nothing
 * when there is nothing to show (count <= 0) so empty nav items stay clean.
 */
export function NeedsYouBadge({ count, tone = 'amber', className }: NeedsYouBadgeProps) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        'text-[10px] font-extrabold px-[7px] py-0.5 rounded-full whitespace-nowrap tabular-nums',
        TONE_CLASS[tone],
        className,
      )}
    >
      {count}
    </span>
  );
}

/** The per-surface counts that feed the sidebar badges. */
export interface OpsBadgeCounts {
  /** Sums into the global "Needs you" total (work-queue escalations). */
  needsYou: number;
  /** Monitor capture-health attention count. */
  monitor: number;
  /** Graph-intake review count. */
  graphIntake: number;
  /** Pending reader coverage requests (F4 red-link demand). */
  coverageRequests: number;
}

const ZERO_BADGES: OpsBadgeCounts = { needsYou: 0, monitor: 0, graphIntake: 0, coverageRequests: 0 };

/**
 * The ONE nav-badge source hook. Every sidebar count reads from here so they all
 * come from one place and the per-item numbers sum to the global "Needs you"
 * total shown in the header.
 *
 * Sources the live counts from GET /api/admin/ops/badges (work-queue escalated /
 * open self-heal tasks / pending graph-intake). Silently degrades to zeros on any
 * error or when the backing stores are off — NeedsYouBadge renders null at 0, so an
 * offline engine keeps the sidebar clean (byte-neutral when nothing needs you).
 */
export function useOpsBadges(): OpsBadgeCounts {
  const { adminFetch } = useAdminApi();
  // Gate the mount fetch behind client auth-readiness (same pattern as the /admin pages,
  // PR #128 / #124). WHY: adminFetch → getAuthHeaders() throws "User is not authenticated"
  // whenever Firebase's auth.currentUser is still null — the few-hundred-ms window on a hard
  // load/refresh before onAuthStateChanged rehydrates the session. The old effect fired
  // immediately, hit that throw, and pinned every badge to ZERO for the whole session (deps
  // were [adminFetch], which is stable, so it never re-ran once auth resolved). Waiting for
  // useAuth() (loading === false ⇒ user populated) and putting `loading` + `user` in the
  // effect deps makes the fetch fire once auth is ready, so badges populate after a hard
  // refresh with no manual navigation.
  const { user, loading } = useAuth();
  const [counts, setCounts] = useState<OpsBadgeCounts>(ZERO_BADGES);

  useEffect(() => {
    // Don't fetch until auth has resolved with a signed-in user — otherwise the token isn't
    // ready and the request would 401/throw and strand the badges at zero.
    if (loading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch<{ counts?: Partial<OpsBadgeCounts> }>('/api/admin/ops/badges');
        if (cancelled) return;
        setCounts({ ...ZERO_BADGES, ...(res.counts || {}) });
      } catch {
        if (!cancelled) setCounts(ZERO_BADGES);
      }
    })();
    return () => { cancelled = true; };
  }, [adminFetch, loading, user]);

  return counts;
}
